import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthService } from '../services/AuthService';
import { RideService } from '../services/RideService';
import { Driver, Ride } from '../types';
import './Dashboard.css';

const DriverDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [driver, setDriver] = useState<Driver | null>(null);
  const [rides, setRides] = useState<Ride[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        const currentUser = AuthService.getCurrentUser();
        if (!currentUser) {
          navigate('/driver-login');
          return;
        }

        const userData = await AuthService.getUserData(currentUser.uid);
        if (!userData || userData.userType !== 'DRIVER') {
          navigate('/driver-login');
          return;
        }

        setDriver(userData as Driver);
        
        // Şoförün yolculuklarını getir
        const driverRides = await RideService.getDriverRides(currentUser.uid);
        setRides(driverRides);
      } catch (error) {
        console.error('Dashboard verisi yüklenirken hata:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await AuthService.logout();
      navigate('/');
    } catch (error) {
      console.error('Çıkış hatası:', error);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <p>Yükleniyor...</p>
      </div>
    );
  }

  if (!driver) {
    return <div>Kullanıcı bulunamadı</div>;
  }

  const completedRides = rides.filter(ride => ride.status === 'COMPLETED');
  const totalEarnings = completedRides.reduce((sum, ride) => sum + (ride.fare || 0), 0);

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <div className="logo">
            <h2>Taxicim Şoför</h2>
          </div>
          <div className="user-info">
            <span>Hoş geldiniz, {driver.firstName} {driver.lastName}</span>
            <button onClick={handleLogout} className="logout-btn">
              Çıkış
            </button>
          </div>
        </div>
      </header>

      <div className="dashboard-content">
        <nav className="dashboard-nav">
          <button 
            className={activeTab === 'overview' ? 'active' : ''}
            onClick={() => setActiveTab('overview')}
          >
            📊 Genel Bakış
          </button>
          <button 
            className={activeTab === 'rides' ? 'active' : ''}
            onClick={() => setActiveTab('rides')}
          >
            🚗 Yolculuklar
          </button>
          <button 
            className={activeTab === 'profile' ? 'active' : ''}
            onClick={() => setActiveTab('profile')}
          >
            👤 Profil
          </button>
        </nav>

        <main className="dashboard-main">
          {activeTab === 'overview' && (
            <div className="overview-tab">
              <h3>Genel Bakış</h3>
              <div className="stats-grid">
                <div className="stat-card">
                  <h4>Toplam Yolculuk</h4>
                  <p className="stat-number">{driver.totalRides || 0}</p>
                </div>
                <div className="stat-card">
                  <h4>Tamamlanan Yolculuklar</h4>
                  <p className="stat-number">{completedRides.length}</p>
                </div>
                <div className="stat-card">
                  <h4>Toplam Kazanç</h4>
                  <p className="stat-number">₺{totalEarnings.toFixed(2)}</p>
                </div>
                <div className="stat-card">
                  <h4>Puan</h4>
                  <p className="stat-number">{(driver.rating || 0).toFixed(1)} ⭐</p>
                </div>
                <div className="stat-card">
                  <h4>Durum</h4>
                  <p className={`status ${driver.isOnline ? 'online' : 'offline'}`}>
                    {driver.isOnline ? 'Çevrimiçi' : 'Çevrimdışı'}
                  </p>
                </div>
                <div className="stat-card">
                  <h4>Bakiye</h4>
                  <p className="stat-number">₺{(driver.balance || 0).toFixed(2)}</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'rides' && (
            <div className="rides-tab">
              <h3>Yolculuk Geçmişi</h3>
              <div className="rides-list">
                {rides.length > 0 ? (
                  rides.map((ride) => (
                    <div key={ride.id} className="ride-card">
                      <div className="ride-info">
                        <p><strong>Başlangıç:</strong> {ride.pickupLocation.address}</p>
                        <p><strong>Varış:</strong> {ride.dropoffLocation.address}</p>
                        <p><strong>Tarih:</strong> {new Date(ride.createdAt).toLocaleDateString('tr-TR')}</p>
                      </div>
                      <div className="ride-details">
                        <p className={`status ${ride.status.toLowerCase()}`}>
                          {ride.status === 'COMPLETED' ? 'Tamamlandı' :
                           ride.status === 'CANCELLED' ? 'İptal Edildi' :
                           ride.status === 'IN_PROGRESS' ? 'Devam Ediyor' : ride.status}
                        </p>
                        <p className="fare">₺{(ride.fare || 0).toFixed(2)}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="no-rides">Henüz yolculuk bulunamadı.</p>
                )}
              </div>
            </div>
          )}

          {activeTab === 'profile' && (
            <div className="profile-tab">
              <h3>Profil Bilgileri</h3>
              <div className="profile-info">
                <div className="info-section">
                  <h4>Kişisel Bilgiler</h4>
                  <p><strong>Ad:</strong> {driver.firstName}</p>
                  <p><strong>Soyad:</strong> {driver.lastName}</p>
                  <p><strong>E-posta:</strong> {driver.email}</p>
                  <p><strong>Telefon:</strong> {driver.phone}</p>
                  <p><strong>Şehir:</strong> {driver.city?.name || 'Belirtilmemiş'}</p>
                </div>
                
                <div className="info-section">
                  <h4>Sürücü Bilgileri</h4>
                  <p><strong>Ehliyet No:</strong> {driver.licenseNumber}</p>
                  <p><strong>Doğrulama:</strong> {driver.isVerified ? 'Doğrulandı' : 'Beklemede'}</p>
                </div>

                <div className="info-section">
                  <h4>Araç Bilgileri</h4>
                  <p><strong>Marka:</strong> {driver.vehicleInfo.brand}</p>
                  <p><strong>Model:</strong> {driver.vehicleInfo.model}</p>
                  <p><strong>Yıl:</strong> {driver.vehicleInfo.year}</p>
                  <p><strong>Plaka:</strong> {driver.vehicleInfo.plate}</p>
                  <p><strong>Renk:</strong> {driver.vehicleInfo.color}</p>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default DriverDashboard; 